import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Brain, 
  Play, 
  Pause, 
  Settings, 
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Zap,
  Shield,
  Activity,
  BarChart3,
  Cpu,
  Target,
  RefreshCw
} from 'lucide-react';

const AILearningControl = () => {
  const [algorithms, setAlgorithms] = useState([]);
  const [aiMetrics, setAiMetrics] = useState({});
  const [trainingHistory, setTrainingHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  // Load AI data on component mount
  useEffect(() => {
    loadAIData();
  }, []);

  const loadAIData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      
      // Load algorithms
      const algorithmsResponse = await fetch('/ai-learning/algorithms', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (algorithmsResponse.ok) {
        const algorithmsData = await algorithmsResponse.json();
        setAlgorithms(algorithmsData.algorithms || []);
      }
      
      // Load AI metrics
      const metricsResponse = await fetch('/ai-learning/metrics', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (metricsResponse.ok) {
        const metricsData = await metricsResponse.json();
        setAiMetrics(metricsData.metrics || {});
      }
      
      // Load training history
      const historyResponse = await fetch('/ai-learning/training/history', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        setTrainingHistory(historyData.history || []);
      }
      
    } catch (err) {
      setError('Failed to load AI data');
      console.error('Error loading AI data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAlgorithmStatusChange = async (algorithmId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/ai-learning/algorithms/${algorithmId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        setMessage(`Algorithm status updated to ${newStatus}`);
        setMessageType('success');
        // Reload algorithms to get updated status
        loadAIData();
      } else {
        setMessage('Failed to update algorithm status');
        setMessageType('error');
      }
    } catch (err) {
      setMessage('Error updating algorithm status');
      setMessageType('error');
      console.error('Error updating algorithm status:', err);
    }
  };

  const handleStartTraining = async (algorithmId) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch('/ai-learning/training', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ algorithm_id: algorithmId })
      });
      
      if (response.ok) {
        setMessage('Training started successfully');
        setMessageType('success');
        // Reload data to get updated training status
        loadAIData();
      } else {
        setMessage('Failed to start training');
        setMessageType('error');
      }
    } catch (err) {
      setMessage('Error starting training');
      setMessageType('error');
      console.error('Error starting training:', err);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'inactive':
        return <XCircle className="w-5 h-5 text-red-400" />;
      case 'training':
        return <Activity className="w-5 h-5 text-blue-400" />;
      case 'error':
        return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      default:
        return <Brain className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-400';
      case 'inactive':
        return 'text-red-400';
      case 'training':
        return 'text-blue-400';
      case 'error':
        return 'text-yellow-400';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'inactive':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'training':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'error':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };



  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
      <div>
        <h1 className="text-3xl font-bold text-white">AI Learning Control</h1>
        <p className="text-gray-400 mt-1">Manage AI learning algorithms and compliance automation</p>
      </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={loadAIData}
            disabled={isLoading}
            className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors flex items-center"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button className="btn-primary flex items-center">
            <Settings className="w-4 h-4 mr-2" />
            Configure AI
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <div>
              <h3 className="text-red-400 font-medium">Error Loading AI Data</h3>
              <p className="text-red-300 text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Message Display */}
      {message && (
        <div className={`border rounded-lg p-4 ${
          messageType === 'success' 
            ? 'bg-green-900/20 border-green-500/50' 
            : 'bg-red-900/20 border-red-500/50'
        }`}>
          <div className="flex items-center space-x-3">
            {messageType === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-400" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-red-400" />
            )}
            <div>
              <p className={`text-sm ${messageType === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                {message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* AI System Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">System Status</p>
              <p className="text-2xl font-bold text-white">Active</p>
            </div>
            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
              <Brain className="w-6 h-6 text-green-400" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm text-green-400">
              <CheckCircle className="w-4 h-4 mr-1" />
              <span>All systems operational</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Current Model</p>
              <p className="text-2xl font-bold text-white">GPT-4</p>
            </div>
            <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Cpu className="w-6 h-6 text-blue-400" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm text-blue-400">
              <TrendingUp className="w-4 h-4 mr-1" />
              <span>{aiMetrics.accuracy || 95}% accuracy</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Training Progress</p>
              <p className="text-2xl font-bold text-white">{aiMetrics.training_progress || 0}%</p>
            </div>
            <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Activity className="w-6 h-6 text-purple-400" />
            </div>
          </div>
          <div className="mt-4">
            <div className="w-full bg-gray-600 rounded-full h-2">
              <div
                className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${aiMetrics.training_progress || 0}%` }}
              ></div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Response Time</p>
              <p className="text-2xl font-bold text-white">{aiMetrics.response_time || '0.3s'}</p>
            </div>
            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
              <Zap className="w-6 h-6 text-green-400" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm text-green-400">
              <Target className="w-4 h-4 mr-1" />
              <span>Target: &lt;0.5s</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Algorithm Management */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Algorithm Management</h2>
          <div className="flex items-center space-x-2">
            <button className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center">
              <Play className="w-4 h-4 mr-2" />
              Start All
            </button>
            <button className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center">
              <Pause className="w-4 h-4 mr-2" />
              Stop All
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {algorithms.map((algorithm) => (
            <motion.div
              key={algorithm.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-4 bg-gray-700 rounded-lg border border-gray-600 hover:border-gray-500 transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-medium text-white mb-1">{algorithm.name}</h3>
                  <p className="text-sm text-gray-400">{algorithm.description}</p>
                </div>
                {getStatusIcon(algorithm.status)}
              </div>
              
              <div className="flex items-center justify-between text-sm mb-3">
                <span className="text-gray-400">Accuracy: {algorithm.accuracy}%</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadgeColor(algorithm.status)}`}>
                  {algorithm.status}
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleAlgorithmStatusChange(algorithm.id, algorithm.status === 'active' ? 'inactive' : 'active')}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    algorithm.status === 'active'
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-gray-600 hover:bg-gray-500 text-gray-300'
                  }`}
                >
                  {algorithm.status === 'active' ? 'Active' : 'Inactive'}
                </button>
                <button className="p-2 text-blue-400 hover:bg-blue-500/20 rounded transition-colors">
                  <Settings className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Training History */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="card"
      >
        <h2 className="text-xl font-semibold text-white mb-6">Training History</h2>
        <div className="space-y-3">
          {trainingHistory.map((training, index) => (
            <motion.div
              key={training.model}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 * index }}
              className="flex items-center justify-between p-4 bg-gray-700 rounded-lg border border-gray-600 hover:border-gray-500 transition-colors"
            >
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-medium text-white">Model {training.model}</h3>
                  <p className="text-sm text-gray-400">Trained {training.date}</p>
                  <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                    <span>Duration: {training.duration}</span>
                    <span>Accuracy: {training.accuracy}%</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <span className="text-sm text-green-400 font-medium">Completed</span>
                <button className="p-2 text-blue-400 hover:bg-blue-500/20 rounded transition-colors" title="Deploy">
                  <Play className="w-4 h-4" />
                </button>
                <button className="p-2 text-green-400 hover:bg-green-500/20 rounded transition-colors" title="View Details">
                  <BarChart3 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* AI Performance Metrics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">Decision Metrics</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Total Decisions</span>
              <span className="text-white font-medium">{(aiMetrics.total_decisions || 0).toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Overall Accuracy</span>
              <span className="text-green-400 font-medium">{aiMetrics.accuracy || 95}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">False Positives</span>
              <span className="text-yellow-400 font-medium">{aiMetrics.false_positives || 2}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">False Negatives</span>
              <span className="text-red-400 font-medium">{aiMetrics.false_negatives || 1}%</span>
            </div>
          </div>
        </div>
        
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">System Performance</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Response Time</span>
              <span className="text-white font-medium">{aiMetrics.response_time || '0.3s'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Uptime</span>
              <span className="text-green-400 font-medium">{aiMetrics.uptime || '99.9%'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Active Algorithms</span>
              <span className="text-blue-400 font-medium">{algorithms.filter(alg => alg.status === 'active').length}/{algorithms.length}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Training Status</span>
              <span className="text-purple-400 font-medium">{aiMetrics.training_status || 'Idle'}</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="card"
      >
        <h2 className="text-xl font-semibold text-white mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="p-4 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-left">
            <div className="flex items-center space-x-3">
              <Brain className="w-6 h-6 text-white" />
              <div>
                <div className="font-medium text-white">Start Training</div>
                <div className="text-sm text-blue-200">Begin new model training</div>
              </div>
            </div>
          </button>
          
          <button className="p-4 bg-green-600 hover:bg-green-700 rounded-lg transition-colors text-left">
            <div className="flex items-center space-x-3">
              <Shield className="w-6 h-6 text-white" />
              <div>
                <div className="font-medium text-white">Security Scan</div>
                <div className="text-sm text-green-200">Run AI security audit</div>
              </div>
            </div>
          </button>
          
          <button className="p-4 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors text-left">
            <div className="flex items-center space-x-3">
              <Settings className="w-6 h-6 text-white" />
              <div>
                <div className="font-medium text-white">Configure</div>
                <div className="text-sm text-purple-200">Adjust AI parameters</div>
              </div>
            </div>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default AILearningControl;
